import React, { GetDerivedStateFromProps } from "react";
import { connect } from "react-redux";
import { push } from "connected-react-router";
import { Tabs, Input, Icon, Row, Col, Tooltip } from "antd";
import styled from "styled-components";
import { isEqual } from "lodash";

import { fetchOrdersAsync, searchOrdersAsync } from "../../orders/actions";
import { MainComponentContainer } from "../../../shared/MainComponentContainer";
import { Order } from "../../../lib/model/Order";
import { PagedList } from "./PagedList";
import { RootState } from "../../../store/rootReducer";
import { StyledTabs } from "../../../shared/ForwardTabs";
import { OrdersFilter } from "../../../lib/model/OrdersFilter";
import { Page } from "../../../lib/model/Page";

const Wrapper = styled.div`
  padding: 30px;

  @media only screen and (max-width: 575px) {
    padding: 10px;
  }
`;

const mapStateToProps = (state: RootState) => ({
  openOrders: state.orders.openOrders,
  completedOrders: state.orders.completedOrders,
  searchResults: state.orders.searchResults,
  isLoading: state.orders.isLoadingOrders,
});

const dispatchProps = {
  push,
  fetchOrders: fetchOrdersAsync.request,
  searchOrders: searchOrdersAsync.request,
};

type Props = ReturnType<typeof mapStateToProps> & typeof dispatchProps;

type State = {
  orders: Page<Order>;
  activeTabKey: string;
  searchQuery: string;
};

class OrdersContainer extends React.Component<Props, State> {
  state: State = {
    orders: this.props.openOrders,
    activeTabKey: "1",
    searchQuery: ''
  };

  static getDerivedStateFromProps: GetDerivedStateFromProps<Props, State> = (props, state): State => {
    if (state.activeTabKey === "1" && isEqual(props.openOrders, state.orders)) {
      return {
        ...state
      };
    }

    if (state.activeTabKey === "2" && isEqual(props.completedOrders, state.orders)) {
      return {
        ...state
      };
    }
      
    return {
      ...state,
      orders: state.activeTabKey === "1" ? props.openOrders : props.completedOrders,
    };
  }

  componentDidMount() {
    this.props.openOrders.results.length === 0 && this.props.fetchOrders({ filter: OrdersFilter.Open });
  }

  goToDetail = (id: string) => {
    this.props.push(`/orders/${id}`);
  };

  onLoadMore = () => {
    const { openOrders, completedOrders } = this.props;
    const { activeTabKey } = this.state;

    this.props.fetchOrders({
      filter: activeTabKey === "1" ? OrdersFilter.Open : OrdersFilter.Completed, 
      after: activeTabKey === "1" ? openOrders.cursors.after : completedOrders.cursors.after, });
  }

  handleTabChange = (key: string) => {  
  const { openOrders, completedOrders } = this.props;

    this.setState(
      { activeTabKey: key },
      () => {
        if (key === "1" && openOrders.results.length === 0) {
          this.props.fetchOrders({ filter: OrdersFilter.Open });
        }
        if (key === "2" && completedOrders.results.length === 0) {
          this.props.fetchOrders({ filter: OrdersFilter.Completed });
        }
      }
    );
  }

  onSearchChange = async(e: any) => {
    const { value } = e.target;
    
    this.setState({ searchQuery: value });

    if (value.length < 3) {
      return;
    }
    
    this.props.searchOrders(value);
  }

  render() {
    const { isLoading, searchResults } = this.props;
    const { orders, searchQuery } = this.state;

    let isSearching = searchQuery.length > 2;
  
    return (
      <MainComponentContainer additionalMarginTop={38}>
        <StyledTabs type="card" onTabClick={this.handleTabChange}>
          <Tabs.TabPane tab="Anstehend" key="1">
            <Wrapper>
              <Row>
                <Col style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 15 }}>
                  <Tooltip 
                    placement="bottom"
                    title="Geben Sie mindestens drei Zeichen ein um Aufträge zu durchsuchen."
                    visible={searchQuery.length === 1 || searchQuery.length === 2 }
                    trigger="focus">
                      <Input
                        value={searchQuery}
                        style={{ width: 300 }}
                        suffix={<Icon type="search" className="certain-category-icon" />}
                        onChange={this.onSearchChange}
                      />
                  </Tooltip>
                </Col>
              </Row>
              <PagedList
                orders={!isSearching? orders : searchResults}
                isLoading={isLoading}
                cursor={!isSearching? orders.cursors.after : undefined}
                onLoadMore={this.onLoadMore}
                goToDetail={this.goToDetail}
              />
            </Wrapper>
          </Tabs.TabPane>
          <Tabs.TabPane tab="Abgeschlossen" key="2">
            <Wrapper>
              <Row>
                <Col style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Input
                    value={searchQuery}
                    style={{ width: 300 }}
                    suffix={<Icon type="search" className="certain-category-icon" />}
                    onChange={this.onSearchChange}
                  />
                </Col>
              </Row>
              <PagedList
                orders={!isSearching? orders : searchResults}
                isLoading={isLoading}
                cursor={!isSearching? orders.cursors.after : undefined}
                onLoadMore={this.onLoadMore}
                goToDetail={this.goToDetail}
              />
            </Wrapper>
          </Tabs.TabPane>
        </StyledTabs>
      </MainComponentContainer>
    );
  };
}

export default connect(
  mapStateToProps,
  dispatchProps
)(OrdersContainer);